import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useImmer } from "use-immer";
import { v4 } from "uuid";

import { WORKFLOW_IDS } from "@intelligems/ig-plugin/utils/workflow-ids";
import { AiNodeContext } from "@intelligems/ig-types/schemas/ai/context-schemas";
import { AnalyticsAudienceType } from "@intelligems/ig-types/schemas/analytics/base";
import { ChatWorkflowRequestBodyDto } from "@intelligems/ig-types/dtos/ai/workflows/chat-workflow";
import { UiStreamAction } from "@intelligems/ig-types/schemas/ai/ui-stream-actions";
import { useGetOrg } from "@src/api/organizations/get-organization";
import { useSitewideAnalyticsChat } from "@src/api/ai/sitewide-analytics/use-sitewide-analytics-chat";
import { useGetSitewideAnalyticsChatSessionHistory } from "@src/api/ai/sitewide-analytics/use-get-sitewide-analytics-chat-session-history";
import { aiSitewideAnalyticsKeys } from "@src/api/queryKeys";
import { SitewideAnalyticsPageName } from "@src/api/types/dashboards";
import { SitewideAnalyticsChatHistory } from "@src/features/ai/components/AiChat/SitewideAnalyticsChatHistory";
import { createUserMessage } from "@src/features/ai/components/AiChat/utils/workflowUtils";
import { ExecutionPanel } from "@src/features/ai/components/WorkflowEditor/execution/ExecutionPanel";
import {
  clearAiChat,
  getAiChat,
  handleAiChatAction,
  restoreChatState,
} from "@src/store/slices/aiChat";
import { getAiChatProps } from "@src/store/slices/global";
import { selectedSitewideDashboardFilters } from "@src/store/slices/sitewideDashboardFilters";
import { useQueryClient } from "@tanstack/react-query";
import { RateConversation } from "@src/features/ai/components/WorkflowEditor/execution/components/RateConversation";
import { OnAiChatSend } from "@src/features/ai/components/WorkflowEditor/execution/types";

interface SitewideAnalyticsChatContext {
  summary: string;
  questions?: string[];
  pageName?: SitewideAnalyticsPageName;
  selectedAudience?: AnalyticsAudienceType;
  orgId?: string;
}

function buildFeature(
  pageName: SitewideAnalyticsPageName,
  selectedAudience?: AnalyticsAudienceType
) {
  switch (pageName) {
    case "performance":
      return { name: "performance" as const };
    case "audience":
      return { name: "audience" as const, audience: selectedAudience };
    case "orderDistribution":
      return { name: "order" as const };
    default:
      return { name: "performance" as const };
  }
}

/** Extracts the summary text from whatever shape initialContext has. */
function getSummaryText(initialContext: unknown): string {
  if (typeof initialContext === "string") {
    return initialContext;
  }
  const ctx = initialContext as SitewideAnalyticsChatContext | null;
  return ctx?.summary ?? "";
}

interface SitewideAnalyticsChatProps {
  onClose?: () => void;
}

export const SitewideAnalyticsChat = ({
  onClose,
}: SitewideAnalyticsChatProps) => {
  const dispatch = useDispatch();
  const org = useGetOrg();
  const queryClient = useQueryClient();

  const sitewideFilters = useSelector(selectedSitewideDashboardFilters);
  const aiChat = useSelector(getAiChat);

  const { initialContext } = useSelector(getAiChatProps<AiNodeContext>);
  const { chatWorkflow } = useSitewideAnalyticsChat();

  const [isNewChat, setIsNewChat] = useImmer(false);
  const [showRating, setShowRating] = useImmer(false);
  const [chatInitialized, setChatInitialized] = useImmer(false);
  const [sessionToLoad, setSessionToLoad] = useImmer<string | null>(null);

  const sessionHistory = useGetSitewideAnalyticsChatSessionHistory({
    sessionId: sessionToLoad,
    orgId: org!.data!.org!.id!,
    enabled: !!sessionToLoad,
  });

  useEffect(() => {
    if (sessionHistory.isSuccess && sessionToLoad && sessionHistory.data) {
      dispatch(restoreChatState(sessionHistory.data));
      setSessionToLoad(null);
    }
  }, [sessionHistory.isSuccess, sessionHistory.data, sessionToLoad]);

  const setSessionId = (sessionId: string) =>
    dispatch(
      handleAiChatAction({
        type: "setSessionId",
        sessionId,
      })
    );

  useEffect(() => {
    if (!aiChat.sessionId) {
      setSessionId(v4());
    }
  }, [aiChat.sessionId]);

  const setupChat = (sessionId: string) => {
    const summaryText = getSummaryText(initialContext);
    if (!summaryText) {
      return;
    }

    const ctx =
      initialContext as unknown as SitewideAnalyticsChatContext | null;
    const questions = ctx?.questions ?? [];

    const actions: UiStreamAction[] = [
      {
        type: "initialize",
        workflowId: WORKFLOW_IDS.SITEWIDE_ANALYTICS_SUMMARY_CHAT,
        sessionId,
        nodeId: v4(),
      },
      {
        type: "addMessage",
        nodeId: v4(),
        nodeName: "Initial Message",
        turnId: v4(),
        message: {
          id: v4(),
          role: "assistant",
          timestamp: Date.now(),
          parts: [
            {
              id: v4(),
              type: "text",
              text: summaryText,
              timestamp: Date.now(),
              // @ts-expect-error
              state: "immediate",
            },
          ],
        },
      },
    ];

    if (questions.length > 0) {
      actions.push({
        type: "setNextAction",
        nextAction: {
          type: "user_input",
          variablePath: "",
          userInput: {
            type: "choice",
            options: questions.map((q) => ({ value: q, label: q })),
          },
        },
      });
    }

    dispatch(handleAiChatAction(actions));

    setIsNewChat(true);
    setChatInitialized(true);
  };

  useEffect(() => {
    if (!chatInitialized && initialContext) {
      setupChat(aiChat.sessionId || v4());
    }
  }, [chatInitialized, initialContext]);

  const onSessionIdChange = (id: string) => {
    dispatch(clearAiChat());
    setChatInitialized(false);
    if (id === "new") {
      const newId = v4();
      setSessionId(newId);
      setupChat(newId);
    } else {
      setIsNewChat(false);
      setSessionToLoad(id);
      setSessionId(id);
    }
  };

  const onSend: OnAiChatSend = {
    chat: (payload: ChatWorkflowRequestBodyDto) => {
      if (!payload.input) {
        return;
      }

      setShowRating(true);

      // Stable id shared between the optimistic user bubble and the server
      // echo so the reducer reconciles by id instead of appending a duplicate.
      const inputMessageId = v4();
      const { message } = createUserMessage(payload.input);
      if (message) {
        message.id = inputMessageId;
      }
      const nodeId = v4();
      const turnId = v4();

      if (message) {
        dispatch(
          handleAiChatAction([
            {
              type: "addMessage",
              turnId,
              message,
              nodeId,
              nodeName: "User Input",
            },
          ])
        );
      }

      const summaryText = getSummaryText(initialContext);
      const ctxObj =
        initialContext as unknown as SitewideAnalyticsChatContext | null;
      const pageName =
        ctxObj?.pageName ??
        (sitewideFilters.pageName as SitewideAnalyticsPageName);
      const orgId = ctxObj?.orgId ?? org.data?.org?.id;
      const selectedAudience = ctxObj?.selectedAudience;

      const shouldStartWorkflow = isNewChat;

      // The llm-chat user prompt template uses {{data.turnInput.content}},
      // which is only populated for text-type inputs. Convert choice selections
      // to text so the LLM receives the question text.
      const workflowInput =
        payload.input?.type === "choice"
          ? { type: "text" as const, content: payload.input.choice }
          : payload.input;

      if (shouldStartWorkflow) {
        setIsNewChat(false);

        // The llm-chat node is stateful — it processes the user's question
        // via `input`, not via initialContext.messages. Pass input alongside
        // workflowId so the node receives the question as its live user turn.
        void chatWorkflow
          .mutate({
            workflowId: WORKFLOW_IDS.SITEWIDE_ANALYTICS_SUMMARY_CHAT,
            sessionId: aiChat.sessionId,
            inputMessageId,
            initialContext: {
              variables: {
                summary: summaryText,
                feature: buildFeature(pageName, selectedAudience),
                filters: sitewideFilters,
                orgId,
              },
            },
            input: workflowInput,
          })
          .then(() => {
            void queryClient.invalidateQueries({
              queryKey: aiSitewideAnalyticsKeys.chatHistory(orgId),
            });
          });
      } else {
        void chatWorkflow.mutate({
          sessionId: aiChat.sessionId,
          inputMessageId,
          input: workflowInput,
        });
      }
    },
    isPending: chatWorkflow.isPending,
  };

  const orgId =
    (initialContext as unknown as SitewideAnalyticsChatContext | null)?.orgId ??
    org.data?.org?.id;

  return (
    <ExecutionPanel
      onClose={onClose}
      onSend={onSend}
      feature="analytics"
      workflowId={WORKFLOW_IDS.SITEWIDE_ANALYTICS_SUMMARY_CHAT}
      showSystemMessages={false}
      showToolCallOutput={false}
      clearReasoningOnMessage={true}
      slots={{
        headerLeft: (
          <SitewideAnalyticsChatHistory
            currentSessionId={aiChat.sessionId ?? ""}
            onSessionIdChange={onSessionIdChange}
            orgId={orgId}
          />
        ),
        promptInputTools: (
          <>{showRating && <RateConversation feedbackTarget="ai" />}</>
        ),
      }}
    />
  );
};
